<template>
  <main>
    <GridContainer
      padding
      class="themenwelten"
    >
      <Breadcrumb :items="breadcrumb" />
      <Heading
        spaces-top
        font-weight="ultra"
        size="lg"
        align="center"
        text-uppercase
      >
        Unsere Themenwelten:<br />Entdecken, Vergleichen, Erleben!
      </Heading>
      <Heading
        spaces-top
        align="center"
        tag="h2"
        size="sm"
        font-weight="regular"
      >
        Hier stellen wir Top-Marken und Hersteller vor, deren Produkte wir hier im Shop anbieten.
      </Heading>
      <section class="themenwelten__pages-container">
        <template v-for="(page, pageIndex) in themenwelten">
          <NuxtLink
            :key="page.path"
            :to="`/${page.path}`"
            class="themenwelten__item"
          >
            <div
              class="themenwelten__item-image"
              :class="`themenwelten__item-image--${page.path}`"
            >
              <CdnImage
                v-if="page.bgImage?.src"
                :src="page.bgImage.src"
                :alt="page.bgImage.alt"
                class="themenwelten__item-background"
                :class="`themenwelten__item-background--${page.path}`"
                :lazy="pageIndex > 3"
              />
              <CdnImage
                v-for="(visual, index) in page.visuals"
                :key="`${visual.alt}-${index}`"
                class="themenwelten__item-visual"
                :class="`themenwelten__item-visual--${page.path} themenwelten__item-visual--${page.path}-${index}`"
                :src="visual.src"
                :alt="visual.alt"
                :lazy="pageIndex > 3"
              />
              <CdnImage
                v-if="page.brand?.logo"
                class="themenwelten__item-logo"
                :src="page.brand.logo"
                :alt="`${page.brand.name} Logo`"
                :image-style="`aspect-ratio: ${page.brand.aspectRatio}`"
                :lazy="pageIndex > 3"
                render-without-figure
              />
            </div>
            <Heading
              tag="h3"
              class="themenwelten__item-title text--dark text--thin"
            >
              <span
                class="link link--unstyled"
                v-html="page.title"
              />
              <span class="themenwelten__item-arrow-symbol">
                <Arrow
                  direction="right"
                  tag="span"
                />
              </span>
            </Heading>
          </NuxtLink>
        </template>
      </section>

      <RecommendedProducts />
    </GridContainer>
  </main>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { getHeader } from '~/helpers/page-header-compositon';
import { Track } from '~/helpers/page-tracking';

import { Arrow, GridContainer, Heading } from '@i22/td-longtail-vue-components';
import Breadcrumb from '~/components/breadcrumb.vue';
import CdnImage from '~/components/cdn-image.vue';
import RecommendedProducts from '~/components/recommended-products.vue';
import { addLogo, getBrandLogo } from '~/lib/brand';

export default defineComponent({
  components: {
    Arrow,
    GridContainer,
    Heading,
    Breadcrumb,
    RecommendedProducts,
    CdnImage,
  },
  setup: () => {
    Track();

    getHeader({
      title: ref('Themenwelten'),
      description: ref(
        'Entdecke die Themenwelten von Top-Marken wie Samsung, Google, Xiaomi & mehr! ➡️ Jetzt stöbern und entdecken!'
      ),
    });

    const breadcrumb = [{ label: 'Themenwelten', to: '/themenwelten' }];

    const themenwelten = [
      {
        path: 'a-good-company',
        name: 'A good company',
        bgImage: {
          src: '/content/content-images/landingpages/themenwelt-overview/backgrounds/3be15c36552411befa966b0de398187b/teaser-agood-bg@2x.jpg',
          alt: '', // intentionally left empty because it's decorative
        },
        visuals: [
          {
            src: '/content/content-images/landingpages/themenwelt-overview/visuals/8249c683c18d8d97f46150daf8e2f55a/teaser-agood-smartphone@2x.png',
            alt: 'Transparente Handyschutzhülle von a good company',
          },
        ],
        title: 'agood – Nachhaltige Handy Cases für jedes Smartphone',
        logo: getBrandLogo('agood'),
        brand: addLogo({ slug: 'agood', name: 'agood company' }),
      },
      {
        path: 'xiaomi',
        name: 'Xiaomi',
        bgImage: {
          src: '/content/content-images/landingpages/themenwelt-overview/backgrounds/f0c365bcb1301296990752fa8810b520/teaser-xiaomi-bg@2x.jpg',
          alt: '',
        },
        visuals: [
          {
            src: '/content/content-images/landingpages/themenwelt-overview/visuals/0aad15d216a31eac2c98df51831e76ef/teaser-xiaomi-geraete@2x.png',
            alt: 'Monitor, Massage Gun und Air Fryer von Xiaomi',
          },
          {
            src: '/content/content-images/landingpages/themenwelt-overview/visuals/7ba1de1f1c5968261c535e3d5bcb264c/teaser-xiaomi-woman@2x.png',
            alt: 'Eine Frau hält ein Samsung-Handy',
          },
        ],
        title: 'Xiaomi – Innovationen und Technik vom Smartphone bis zu Haushalts-Gadgets',
        brand: addLogo({ slug: 'xiaomi', name: 'Xiaomi' }),
      },
      {
        path: 'panzerglass',
        name: 'PanzerGlass',
        bgImage: {
          src: '/content/content-images/landingpages/themenwelt-overview/backgrounds/8900885cd4e2f4c29801cd7672e62ab9/bg-panzerglass@2x.jpg',
          alt: '',
        },
        visuals: [
          {
            src: '/content/content-images/landingpages/themenwelt-overview/visuals/079de3e1f9dd605ce5f8543333c00e6b/panzerglass-themenwelt@2x.png',
            alt: 'PanzerGlass - Displayschutz-Produkte für Tablets und Smartphones',
          },
        ],
        title: 'PanzerGlass – Displayschutz',
        brand: addLogo({ slug: 'panzerglass', name: 'PanzerGlass' }),
      },
      /*
      // Diese Themenwelt wird überarbeitet.
      {
        path: 'google',
        name: 'Google',
        bgImage: {
          src: '/content/content-images/landingpages/themenwelt-overview/backgrounds/d65461ddbe8409308987d8797577182b/teaser-google-bg@2x.jpg',
          alt: '',
        },
        visuals: [
          {
            src: '/content/content-images/landingpages/themenwelt-overview/visuals/90fbc56eca8f6034372ff8b4788a6bf1/teaser-google-case@2x.png',
            alt: 'Google Pixel Case',
          },
          {
            src: '/content/content-images/landingpages/themenwelt-overview/visuals/7378a18349d265e0fdfbb012835bd6ed/pixel-buds-pro-vergleich.png',
            alt: 'Pixel Buds Pro',
          },
        ],
        title: 'Google Pixel Zubehör – Kopfhörer, Cases und mehr',
        brand: addLogo({ slug: 'google', name: 'Google' }),
      }, */
      {
        path: 'samsung',
        name: 'Samsung',
        bgImage: {
          src: '/content/content-images/landingpages/themenwelt-overview/backgrounds/0120d6447530dd10d5c3a327bc1c0728/teaser-samsung-bg@2x.jpg',
          alt: '',
        },
        visuals: [
          {
            src: '/content/content-images/homepage/theme-teasers/3e8ed298bbf3613c9f4b953cbabb3744/samsung-teaser-produkte@2x.png',
            alt: 'Eine Auswahl von Samsung-Geräten: Galaxy Watch7, Galaxy S25 Ultra, etc.',
          },
        ],
        title: 'Samsung – Alles aus der Samsung Galaxy Family',
        brand: addLogo({ slug: 'samsung', name: 'Samsung' }),
      },
      {
        path: 'anker',
        name: 'Anker',
        bgImage: {
          src: '/content/content-images/landingpages/themenwelt-overview/backgrounds/e4f23df43cbd6008479dfb82d2f7e36d/teaser-anker-bg@2x.jpg',
          alt: '',
        },
        visuals: [
          {
            src: '/content/content-images/landingpages/themenwelt-overview/visuals/6147c2d37280d1a6e006719927cb2ad1/teaser-anker-nano-stecker@2x.png',
            alt: 'Anker Charger',
          },
          {
            src: '/content/content-images/landingpages/themenwelt-overview/visuals/b525aa9b414d081152c4129185b08580/teaser-anker-powerbank@2x.png',
            alt: 'Anker Powerbank',
          },
          {
            src: '/content/content-images/landingpages/themenwelt-overview/visuals/fe176487d74149904995a1e9c28c03e0/teaser-anker-adapter@2x.png',
            alt: 'Anker Adapter',
          },
        ],
        title: 'Anker – Die Nr. 1 für mobiles Laden',
        brand: addLogo({ slug: 'anker', name: 'Anker' }),
      },
      {
        path: 'smart-home-entdecken',
        name: 'Smart Home',
        bgImage: {
          src: '/content/content-images/landingpages/themenwelt-overview/backgrounds/57b582f9eebdcd2a04fee651dc9e313b/smarthome-themenwelt-teaser-bg@2x.jpg',
          alt: '',
        },
        visuals: [
          {
            src: '/content/content-images/landingpages/themenwelt-overview/visuals/b76176ba33fe40087b9433c9097a875c/smarthome-themenwelt-teaser-geraete@2x.png',
            alt: 'Smart Home Gerätevielfalt',
          },
        ],
        title: 'Smart Home entdecken ',
      },
      {
        path: 'soundcore',
        name: 'soundcore',
        visuals: [
          {
            src: '/content/content-images/landingpages/themenwelt-overview/visuals/d8021e992d712d7606f2f1e1ccfd8b09/teaser-themenwelt-soundcore-geraete.png',
            alt: 'soundcore Geräte',
          },
        ],
        title: 'soundcore – MEHR HÖREN. MEHR FÜHLEN.',
        brand: addLogo({ slug: 'soundcore', name: 'soundcore logo' }),
      },
    ];

    return { breadcrumb, themenwelten };
  },
  head: {},
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.themenwelten {
  padding-bottom: 8 * $grid-base;

  &__pages-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5 * $grid-base;
    margin-top: 6 * $grid-base;
    margin-bottom: 10 * $grid-base;
    padding: 0 $grid-base;

    @include mq($mq-sm) {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 2 * $grid-base;
      align-items: flex-start;
      max-width: 1428px;
    }
  }

  &__item {
    width: 100%;
    @include mq($mq-sm) {
      width: calc(50% - 12px);
    }

    &-image {
      display: grid;
      margin-bottom: $grid-base;
      position: relative;
      overflow: hidden;
      transition: border-radius 0.3s ease;
      width: 100%;
      aspect-ratio: 320/199;

      &--soundcore {
        background: linear-gradient(to top right, var(--color-bg), var(--color-grey-200));
      }
    }

    &-background {
      grid-area: 1 / 1 / 2 / 2;
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: filter 0.3s ease;

      :deep(img) {
        width: 100%;
        height: 100%;
      }

      &--smart-home-entdecken {
        filter: blur(8px);
      }
    }

    &-visual {
      grid-area: 1 / 1 / 2 / 2;
      z-index: 1;
      transition: transform 0.3s ease;

      :deep(img) {
        width: 100%;
      }

      &--a-good-company {
        width: 88%;
        position: absolute;
        top: 11%;
        right: -25px;

        :deep(img) {
          aspect-ratio: 320/227;
        }
      }

      &--xiaomi {
        //background devices
        &-0 {
          position: absolute;
          width: 59%;
          left: -10%;
          top: 25%;

          :deep(img) {
            aspect-ratio: 128/75;
          }
        }

        //model holding phone
        &-1 {
          width: 80%;
          position: absolute;
          right: -6%;
          bottom: 0;

          :deep(img) {
            aspect-ratio: 320/253;
          }
        }
      }

      &--google {
        // phonecase
        &-0 {
          width: 72%;
          position: absolute;
          bottom: -8%;

          :deep(img) {
            aspect-ratio: 80/61;
          }
        }

        // earbuds
        &-1 {
          width: 45%;
          position: absolute;
          right: 12%;
          top: 5%;

          :deep(img) {
            aspect-ratio: 1/1;
          }
        }
      }

      &--samsung {
        position: absolute;
        width: 70%;
        bottom: -2%;
        right: 10%;

        :deep(img) {
          aspect-ratio: 464 / 369;
        }
      }

      &--smart-home-entdecken {
        position: absolute;
        width: 60%;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        :deep(img) {
          aspect-ratio: 320/277;
        }
      }

      &--anker {
        // nanostecker
        &-0 {
          position: absolute;
          left: 23%;
          bottom: 14%;
          width: 27%;
          z-index: 2;

          :deep(img) {
            aspect-ratio: 201/169;
          }
        }

        // powerbank
        &-1 {
          position: absolute;
          width: 53%;
          top: 21%;
          left: 29%;

          :deep(img) {
            aspect-ratio: 1/1;
          }
        }

        // adapter
        &-2 {
          width: 34%;
          position: absolute;
          right: 9%;
          top: 20%;

          :deep(img) {
            aspect-ratio: 93/77;
          }
        }
      }

      &--soundcore-0 {
        align-self: flex-end;
      }
    }

    &-logo {
      position: absolute;
      top: $grid-base;
      left: $grid-base;
      //width: 30%;
      transition: transform 0.3s ease;
      height: 16%;
      max-width: 40%;
      object-fit: contain;

      @include mq($mq-md) {
        top: 2 * $grid-base;
        left: 2 * $grid-base;
      }
    }

    &-title {
      @include font-size(18px, 24px);
    }

    &-arrow-symbol {
      position: relative;
      gap: 2px;
      bottom: 5px;
      display: inline-flex;
      &::before {
        content: '';
        border-top: 1.5px solid var(--color-font);
        position: relative;
        align-self: center;
        min-width: 0;
        width: auto;
        transition: min-width 0.3s ease-out;
      }

      .arrow {
        position: relative;
        width: 0.7em;
        height: 0.7em;
      }
    }

    &:hover {
      .themenwelten__item-arrow-symbol {
        &::before {
          min-width: $grid-base;
        }
      }

      .themenwelten__item-image {
        border-radius: 2 * $grid-base;
      }

      .themenwelten__item-background {
        filter: brightness(0.8);

        &--smart-home-entdecken {
          filter: blur(0) brightness(0.8);
        }
      }

      .themenwelten__item-visual {
        &--a-good-company {
          transform: scale(1.05);
        }

        &--xiaomi {
          &-0 {
            transform: scale(1.05);
          }

          &-1 {
            transform: scale(0.95) translate(5%, 5%);
          }
        }

        &--displex {
          transform: scale(1.07);
        }

        &--google {
          transform: scale(1.07) rotate(2deg);
        }

        &--samsung {
          transform: scale(1.05);
        }

        &--anker {
          &-0 {
            transform: scale(1.05) translate(5%, 5%);
          }

          &-1 {
            transform: scale(1.05) rotate(-3deg) translate(-3%, -10%);
          }

          &-2 {
            transform: scale(1.05) translate(5%, -5%);
          }
        }

        &--smart-home-entdecken {
          transform: scale(1.1) translate(-45%, -45%);
        }
      }

      .themenwelten__item-logo {
        transform: scale(1.05);
      }
    }
  }
}
</style>
